import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';

import { HeaderComponent } from '../../../shared/components/header/header.component';
import { FooterActionComponent } from '../../../shared/components/footer-action/footer-action.component';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { LoadingBoxDefaultListComponent } from '../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { AlertCardComponent } from '../../../shared/components/alert-card/alert-card.component';
import { ActionCardComponent } from '../../../shared/components/action-card/action-card.component';

import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { IntegracaoZoppyService } from '../../../shared/services/apis/integracao-zoppy/integracao-zoppy.service';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';

import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';

import { GetConfigZoppyResponse, LogZoppy } from '../../../shared/services/responses/integracao-zoppy/get-config-zoppy.response';
import { TipoLog } from '../../../shared/services/responses/integracao-zoppy/get-tipos-log.response';
import { UpdateConfigZoppyRequest } from '../../../shared/services/requests/integracao-zoppy/update-config-zoppy.request';
import { IntegracaoZoppyDetalheDialogComponent } from './integracao-zoppy-detalhe-dialog/integracao-zoppy-detalhe-dialog.component';
import { TipoLogService } from '../../../shared/services/apis/integracao-zoppy/tipo-log.service';
import { IntegracaoZoppySaibaMaisDialogComponent } from './integracao-zoppy-saiba-mais-dialog/integracao-zoppy-saiba-mais-dialog.component';

@Component({
    selector: 'app-integracao-zoppy',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSlideToggleModule,
        MatButtonModule,
        MatIconModule,
        MatTableModule,
        MatProgressSpinnerModule,
        HeaderComponent,
        FooterActionComponent,
        LoadingBoxComponent,
        LoadingBoxDefaultListComponent,
        EmptyListComponent,
        AlertCardComponent,
        ActionCardComponent,
        MatSelectModule,
        MatDatepickerModule,
        MatNativeDateModule
    ],
    templateUrl: './integracao-zoppy.component.html',
    styleUrls: ['./integracao-zoppy.component.css']
})
export class IntegracaoZoppyComponent implements OnInit {

    form: FormGroup;
    filterForm: FormGroup;
    isLoading = false;
    isLoadingLogs = false;
    isSaving = false;
    moduloEnum = ModuloEnum.Integracao;
    funcionalidadeEnum = FuncionalidadeEnum.Integracao_Conector_Zoppy;

    tiposLog: TipoLog[] = [];

    allLogs: LogZoppy[] = [];
    dataSource = new MatTableDataSource<LogZoppy>();
    paginaAtual = 0;
    ultimaPagina = false;
    ultimaSincronizacao: Date | null = null;

    displayedColumns: string[] = ['idLog', 'datetimeInclusion', 'descricaoTipoLog', 'zoppyUrl', 'actions'];

    constructor(
        private formBuilder: FormBuilder,
        private integracaoZoppyService: IntegracaoZoppyService,
        private tipoLogService: TipoLogService,
        private alertService: AlertService,
        private router: Router,
        private dialog: MatDialog
    ) {
        this.form = this.formBuilder.group({
            isAtivo: [false],
            chaveZoppy: ['', Validators.required],
            isResulth: [{ value: true, disabled: true }]
        });

        this.filterForm = this.formBuilder.group({
            dataInicial: [null],
            dataFinal: [null],
            idTipoLog: [null]
        });
    }

    ngOnInit(): void {
        this.loadTiposLog();
        this.loadConfig();
    }

    loadTiposLog(): void {
        this.tipoLogService.getTiposLog().subscribe({
            next: (response: any) => {
                if (!response.isError) {
                    this.tiposLog = response.tiposLog;
                }
            }
        });
    }

    filtrarLogs(): void {
        this.paginaAtual = 0;
        this.loadConfig();
    }

    loadConfig(): void {
        this.isLoading = true;
        const filtros = this.filterForm.value;
        const dtIni = filtros.dataInicial ? new Date(filtros.dataInicial).toISOString() : null;
        const dtFim = filtros.dataFinal ? new Date(filtros.dataFinal).toISOString() : null;

        this.integracaoZoppyService.getConfig(0, dtIni, dtFim, filtros.idTipoLog).subscribe({
            next: (response: GetConfigZoppyResponse) => {
                this.isLoading = false;
                if (response.isError) {
                    this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
                    return;
                }

                this.form.patchValue({
                    isAtivo: response.config.isAtivo,
                    chaveZoppy: response.config.chaveZoppy,
                    isResulth: response.config.isResulth
                });

                this.ultimaSincronizacao = response.ultimaSincronizacao;

                this.ultimaPagina = true;
                if (response.logs?.length > 0) {
                    this.allLogs = response.logs;
                    this.ultimaPagina = false;
                } else {
                    this.allLogs = [];
                }
                this.dataSource.data = this.allLogs;
            },
            error: () => {
                this.isLoading = false;
                this.alertService.show('Aviso', 'Falha na comunicação com o servidor.', AlertType.warning);
            }
        });
    }

    proximaPagina(): void {
        this.paginaAtual++;
        this.loadLogs();
    }

    loadLogs(): void {
        this.isLoadingLogs = true;
        const filtros = this.filterForm.value;
        const dtIni = filtros.dataInicial ? new Date(filtros.dataInicial).toISOString() : null;
        const dtFim = filtros.dataFinal ? new Date(filtros.dataFinal).toISOString() : null;

        this.integracaoZoppyService.getConfig(this.paginaAtual, dtIni, dtFim, filtros.idTipoLog).subscribe({
            next: (response: GetConfigZoppyResponse) => {
                this.isLoadingLogs = false;
                if (response.isError) {
                    this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
                    return;
                }

                this.ultimaPagina = true;
                if (response.logs?.length > 0) {
                    this.allLogs.push(...response.logs);
                    this.ultimaPagina = false;
                }
                this.dataSource.data = this.allLogs;
                this.ultimaSincronizacao = response.ultimaSincronizacao;
            },
            error: () => {
                this.isLoadingLogs = false;
                this.alertService.show('Aviso', 'Falha na comunicação com o servidor.', AlertType.warning);
            }
        });
    }

    salvar(): void {
        this.form.markAllAsTouched();

        if (this.form.invalid) {
            this.alertService.show('Atenção', 'Preencha os campos obrigatórios.', AlertType.error);
            return;
        }

        this.isSaving = true;
        const request: UpdateConfigZoppyRequest = {
            isAtivo: this.form.get('isAtivo')?.value,
            chaveZoppy: this.form.get('chaveZoppy')?.value
        };

        this.integracaoZoppyService.updateConfig(request).subscribe({
            next: (response: any) => {
                this.isSaving = false;
                if (response.isError) {
                    this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
                    return;
                }
                this.alertService.show('Sucesso', 'Configurações salvas com sucesso!', AlertType.success);
            },
            error: (error: any) => {
                this.isSaving = false;
                this.alertService.show('Aviso', error || 'Falha na comunicação com o servidor.', AlertType.warning);
            }
        });
    }

    voltar(): void {
        this.router.navigate(['/integracao']);
    }

    formatDate(date: Date | string | null): string {
        if (!date) return 'Nenhuma sincronização registrada';
        const d = new Date(date);
        return d.toLocaleString('pt-BR');
    }

    truncateText(text: string, maxLength: number = 80): string {
        if (!text) return '';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    }

    abrirDetalhe(log: LogZoppy): void {
        this.dialog.open(IntegracaoZoppyDetalheDialogComponent, {
            data: { log },
            width: '700px',
            maxHeight: '90vh'
        });
    }

    getBadgeClass(idTipoLog: number): string {
        switch (idTipoLog) {
            case 1: return 'badge-vermelho'; // Erro
            case 2: return 'badge-verde';    // Sucesso
            case 3: return 'badge-amarelo';  // Alerta
            default: return 'badge-cinza';
        }
    }

    abrirModalSaibaMais(): void {
        this.dialog.open(IntegracaoZoppySaibaMaisDialogComponent, {
            width: '900px',
            maxHeight: '90vh'
        });
    }
}
